import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import Layout from '@/components/layout/Layout';

const INTERNAL_ADMIN_ROLES = new Set([
  'admin',
  'super_admin',
  'manager',
  'maintenance_supervisor',
  'maintenance_coordinator',
  'maintenance_director',
  'executive_maintenance_director',
  'regional_maintenance_director',
  'site_maintenance_manager',
  'property_manager',
  'executive_property_manager',
  'regional_property_manager',
]);

// Protected Route Wrapper with loading state
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" message="Checking authentication..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Vendor-Only Route Wrapper with loading state - Mobile PWA Only
export const VendorOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading vendor access..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/vendor-auth" replace />;
  }

  // Only allow legacy vendor users to access vendor routes
  if (user.role === 'vendor_tech' || user.role === 'vendor_admin' || user.role === 'vendor_manager') {
    return <>{children}</>;
  }

  // Redirect non-vendor users to operations center
  return <Navigate to="/operations" replace />;
};

// Maintenance-Only Route Wrapper with loading state - Mobile PWA Only
export const MaintenanceOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading maintenance access..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Only allow maintenance users to access maintenance routes
  if (['maintenance_tech', 'maintenance_supervisor', 'maintenance_coordinator', 'maintenance_director'].includes(user.role)) {
    return <>{children}</>;
  }

  // Redirect non-maintenance users to operations center
  return <Navigate to="/operations" replace />;
};

// Non-Vendor Route Wrapper with loading state - Desktop/Tablet/Mobile Compatible
export const NonVendorRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading platform access..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Only field technicians are restricted to mobile-only apps
  // Supervisors, coordinators, directors, and admins access desktop SaaS
  const mobileOnlyRoles = [
    'vendor_tech',       // Vendor Tech Mobile PWA (field technicians only)
    'maintenance_tech',  // Maintenance Tech Mobile PWA (field technicians only)
  ];

  if (!mobileOnlyRoles.includes(user.role)) {
    return <>{children}</>;
  }

  // Field vendor technicians use mobile PWA
  if (user.role === 'vendor_tech') {
    return <Navigate to="/vendor-tech" replace />;
  }

  // Field maintenance technicians use mobile PWA
  if (user.role === 'maintenance_tech') {
    return <Navigate to="/maintenance-team" replace />;
  }

  // Fallback redirect
  return <Navigate to="/operations" replace />;
};

// Internal/Admin-only wrapper for internal analytics pages
export const InternalAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading internal access..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!INTERNAL_ADMIN_ROLES.has(user.role)) {
    return <Navigate to="/operations" replace />;
  }

  return <>{children}</>;
};
